import ufeApi from 'services/api/ufeApi';
import { gqlClient } from 'services/api/gql/gqlClient';
import { ReturnReasons } from 'constants/gql/queries/checkout/returnReasons.gql';

// https://confluence.sephora.com/wiki/pages/viewpage.action?spaceKey=ILLUMINATE&title=Reason+Codes+API

/** @returns {boolean} Whether OIS returns migration is enabled */
const isOisEnabled = () =>
    !!(Sephora?.configurationSettings?.oisMigration?.global?.isEnabled && Sephora?.configurationSettings?.oisMigration?.returns?.isEnabled);

/**
 * Fetches the list of return reason codes for self-service returns.
 * Routes to OIS GraphQL (ReturnReasons) or legacy OXS REST based on oisMigration feature flags.
 *
 * @param {Object} [params={}] - Input variables forwarded to the OIS GraphQL query (e.g. { orderId, returnType }).
 *   Ignored on the OXS REST path, which always calls `/api/selfReturn/returnReasons` with no query string.
 * @returns {Promise<Object>} On the OIS path: resolves with `{ responseCode, message, returnReasons }`.
 *   On the OXS path: resolves with `{ returnReasons, responseStatus }` (as returned by `ufeApi.makeRequest()`).
 * @throws {Error} Throws with errorCode=-1 and errorMessages for OIS non-SUCCESS responses
 * @throws {Object} Throws with the response data merged with `responseStatus` for OXS errors
 */
const getReturnReasons = async (params = {}) => {
    const oxsUrl = '/api/selfReturn/returnReasons';

    if (isOisEnabled()) {
        const data = await gqlClient.query({
            operationName: 'ReturnReasons',
            query: ReturnReasons,
            variables: { input: params }
        });

        const result = data?.returnReasons;

        if (!result) {
            throw new Error('Get return reasons failed: unexpected response shape');
        }

        if (result.responseCode && result.responseCode !== 'SUCCESS') {
            const error = new Error(result.message || 'Get return reasons failed');
            error.errorCode = -1;
            error.responseCode = result.responseCode;
            error.errorMessages = result.message ? [result.message] : [];

            throw error;
        }

        return result;
    }

    return ufeApi.makeRequest(oxsUrl, { method: 'GET' });
};

export default getReturnReasons;
